import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext(null);
const CART_STORAGE_KEY = 'photomatchCartItems';

const normalizeCartItem = (item) => {
  if (!item || item.id == null) return null;

  return {
    id: item.id,
    eventId: item.eventId ?? null,
    name: item.name || item.title || item.filename || `Photo ${item.id}`,
    filename: item.filename || '',
    price: Number(item.price || 0),
  };
};

const makeCartItem = (photo, eventId) => normalizeCartItem({
  id: photo.id,
  eventId,
  name: photo.name || photo.title || photo.filename || `Photo ${photo.id}`,
  filename: photo.filename,
  price: Number(photo.price || 0),
});

const loadStoredCart = () => {
  try {
    const storedItems = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || '[]');
    if (!Array.isArray(storedItems)) return [];

    return storedItems
      .map(normalizeCartItem)
      .filter(Boolean);
  } catch {
    localStorage.removeItem(CART_STORAGE_KEY);
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadStoredCart);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((photo, eventId) => {
    setItems(prev => {
      if (prev.some(item => item.id === photo.id)) return prev;

      const item = makeCartItem(photo, eventId);
      return item ? [...prev, item] : prev;
    });
  }, []);

  const removeItem = useCallback((photoId) => {
    setItems(prev => prev.filter(item => item.id !== photoId));
  }, []);

  const toggleItem = useCallback((photo, eventId) => {
    setItems(prev => {
      if (prev.some(item => item.id === photo.id)) {
        return prev.filter(item => item.id !== photo.id);
      }

      const item = makeCartItem(photo, eventId);
      return item ? [...prev, item] : prev;
    });
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const value = useMemo(() => ({
    items,
    count: items.length,
    total: items.reduce((sum, item) => sum + Number(item.price || 0), 0),
    addItem,
    removeItem,
    toggleItem,
    clearCart,
  }), [addItem, clearCart, items, removeItem, toggleItem]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};
